import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { TranslationService } from "../../services/TranslationService";
import { Redirect, useLocation, useHistory } from "react-router-dom";
import TabPosition from "../shared/TabPosition";
import ClientService, {
  ClientFields,
  getClientDefaultFieldProperty,
  getClientFieldDisplayValue,
  getClientFieldIsDefault
} from "../../services/ClientService";
import ConsolidatedService from "../../services/ConsolidatedService";
import { ToastService } from "../shared/bootstrap/Toast";
import CompanyService, {
  DataType,
  Entities
} from "../../services/CompanyService";
import { Client } from "./entities/ClientGetResponse";
import {
  formatCurrency,
  formatCurrencyPortal,
  formatDateDigits,
  formatIntizaOldDate,
  formatNumber
} from "../../utils/FormatUtils";
import whatsappIco from "../../assets/img/ico-whatsapp.png";
import Loading from "../shared/components/Loading";
import InvoiceList from "./invoice/InvoiceListConsolidated";
import { EntityField, sortEntityFields } from "../shared/entities/EntityField";
import ClientDetailContext, {
  ClientDetailProvider
} from "./ClientDetailContext";
import { ClientDetailLeftMenuButtons } from "./components/ClientDetailLeftMenuButtonsConsolidated";
import { DatePickerEditor, NumberInput } from "../shared/components/Editors";
import { Helmet } from "react-helmet-async";
import ModalService from "../shared/bootstrap/modal/Modal";
import TooltipComponent from "../shared/TooltipComponent";
import BreadcrumbHistory from "../shared/BreadcrumbHistory";
import { ClientIterator } from "./ClientIterator";
import Dropdown from "../shared/components/Dropdown";
import { StorageService } from "../../services/StorageService";
import { Cast } from "../../utils/Utils";
import { handleErrorWithToast } from "../../utils/RequestUtils";
import { FixToTop } from "../shared/FixToTop";
import AuthenticationContext from "../shared/AuthenticationContext";
import { ConsolidateClientCompany } from "../../entities/consolidated/ConsolidatedClientDetailResponse";

export enum ModalTypeDefault {
  Email = "1",
  Comments = "2",
  Tasks = "3",
  SMS = "4",
  WhastApp = "6"
}

export const getClientFieldDisplayValueHtml = (
  field: EntityField,
  client: Client,
  highlight: boolean,
  copyText?: (text: string) => void,
  currencyId: number = CompanyService.getDefaultCurrencyId(),
  tooltipOrigin: boolean = false
) => {
  let displayValue = getClientFieldDisplayValue(field, client);

  if (field.id === ClientFields.followupMail) {
    return (
      <>
        {client.followUpMail}
        {copyText && (
          <TooltipComponent
            title={TranslationService.translate.CopyToClipboard}
            placement="right"
          >
            <i
              className="fal fa-copy is-link px-2"
              onClick={() => {
                copyText(displayValue);
              }}
            ></i>
          </TooltipComponent>
        )}
      </>
    );
  }

  if (!displayValue) {
    return undefined;
  }

  switch (field.type) {
    case DataType.Date:
      return formatDateDigits(new Date(displayValue));
    case DataType.Link:
      return highlight ? (
        <>
          <a
            target="_blank"
            rel="noopener noreferrer"
            className="overflow-hidden"
            href={displayValue}
          >
            {displayValue}
          </a>
          <a target="_blank" rel="noopener noreferrer" href={displayValue}>
            <i className="far fa-external-link ms-2"></i>
          </a>
        </>
      ) : (
        <a target="_blank" rel="noopener noreferrer" href={displayValue}>
          {displayValue}
          <i className="far fa-external-link ms-2"></i>
        </a>
      );
    case DataType.Number:
      displayValue = formatNumber(displayValue);
      break;
    case DataType.Currency:
      return <>{formatCurrency(displayValue, currencyId)}</>;
    case DataType.Phone:
      return highlight ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <a href={"tel:" + CompanyService.getPhoneLink(displayValue)}>
            {displayValue}
          </a>
          {field.Whatsapp && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={CompanyService.getWhatsappUrl(displayValue)}
            >
              <img
                src={whatsappIco}
                width="20"
                height="20"
                alt="whatsapp icon"
              />
            </a>
          )}
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <a
            href={"tel:" + CompanyService.getPhoneLink(displayValue)}
            className={`col-auto ${tooltipOrigin ? " ms-3" : ""}`}
          >
            {displayValue}
          </a>
          {field.Whatsapp && (
            <a
              target="_blank"
              className={`col-auto ${tooltipOrigin ? " ms-2" : ""}`}
              rel="noopener noreferrer"
              href={CompanyService.getWhatsappUrl(displayValue)}
            >
              <img
                src={whatsappIco}
                width="20"
                height="20"
                alt="whatsapp icon"
              />
            </a>
          )}
        </div>
      );
  }
  return highlight ? (
    <TooltipComponent title={displayValue} placement="bottom">
      {displayValue}
    </TooltipComponent>
  ) : (
    <>{displayValue}</>
  );
};

const ClientFieldDisplayByContent = ({
  title,
  value,
  link,
  isMail,
  tooltip,
  copyText,
  personId
}: {
  title: string;
  value: string | JSX.Element;
  link?: string;
  isMail?: boolean;
  tooltip?: string;
  copyText?: (text: string) => void;
  personId?: number;
}) => {
  const [urlSite, setUrlSite] = useState("");
  const [loading, setLoading] = useState(false);

  const copyTextUrlSite = async (personId: number) => {
    if (urlSite === "") {
      try {
        setLoading(true);
        const result = await ClientService.getShortUrlCustomerSite(personId);
        if (result instanceof Error) {
          setLoading(false);
          ToastService.showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
          return;
        }

        setUrlSite(result.customerSite);
        copyText!(result.customerSite);
        setLoading(false);
      } catch (error) {
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
      }
    } else {
      copyText!(urlSite);
    }
  };

  return (
    <li className="row aboutCompanyItem">
      <h5 className="col-12">{title} </h5>
      <div className="col-12 pe-0" style={{ verticalAlign: "middle" }}>
        {link && (
          <a
            className="ellipsis-oneline d-inline"
            href={(isMail ? "mailto:" : "") + link}
            rel="noreferrer"
            target="_blank"
          >
            {value}
            <i className="far fa-external-link ms-2"></i>
          </a>
        )}
        {link && copyText && isMail && (
          <TooltipComponent
            title={TranslationService.translate.CopyToClipboard}
            placement="right"
          >
            <i
              className="fal fa-copy is-link px-2"
              onClick={() => {
                copyText(link);
              }}
            />
          </TooltipComponent>
        )}
        {link && copyText && !isMail && (
          <TooltipComponent
            title={TranslationService.translate.CopyToClipboard}
            placement="right"
          >
            <i
              className="fal fa-copy is-link px-2"
              onClick={() => {
                copyTextUrlSite(personId!);
              }}
            />
          </TooltipComponent>
        )}
        {!link && <label>{value}</label>}
        {loading && !isMail ? (
          <i className="fa-fw fa-solid fa-spinner-third fa-spin third ms-1"></i>
        ) : (
          <></>
        )}
      </div>
    </li>
  );
};

const copyText = (text: string) => {
  navigator.clipboard.writeText(text);
  ToastService.showToast(
    TranslationService.translate.CopiedToClipboard,
    undefined,
    "success"
  );
};

const ClientFieldDisplay = ({
  field,
  client,
  showAnyway = false,
  onClientSave,
  readOnly = false
}: {
  field: EntityField;
  client: Client;
  showAnyway?: boolean;
  onClientSave?: () => void;
  readOnly?: boolean;
}) => {
  const title = field.title;
  const [value, setValue] = useState(() => getValue(field, client) || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputValue = useRef<string>(value ?? "");

  useEffect(() => {
    setValue(() => getValue(field, client));

    return () => {
      setValue(undefined);
    };
  }, [client, field]);

  if (!value && !showAnyway) {
    return <></>;
  }
  const onSave = async () => {
    setSaving(true);
    if (getClientFieldIsDefault(field.id)) {
      const key = getClientDefaultFieldProperty(field.id);
      Cast<Record<string, string>>(client)[key] = inputValue.current;
    } else {
      const additional = client.additionals.find(
        (x) => x.id === field.id || x["Id" as keyof typeof x] === field.id
      );
      if (additional) {
        additional.value = inputValue.current;
      } else {
        const additional = { id: field.id, value: inputValue.current };
        client.additionals.push(additional);
      }
    }

    await handleErrorWithToast(
      ClientService.setField(
        client.PersonId.toString(),
        field.id,
        inputValue.current
      ),
      () => setSaving(false)
    );

    setValue(inputValue.current);
    setSaving(false);
    setEditing(false);
    onClientSave && onClientSave();
  };
  const dropdownItems =
    field.type !== DataType.List
      ? []
      : CompanyService.getAdditionalDefinitions()
          .find((x) => x.AdditionalDefinitionID === field.id)!
          .AdditionalDefinitionItems.map((x) => ({
            text: x.Value,
            value: x.AdditionalDefinitionItemID
          }));
  const dropdownItemValue =
    dropdownItems.find((x) => x.value?.toString() === value?.toString())
      ?.value || [];

  return (
    <li className="row aboutCompanyItem">
      <h5 className="col-12">{title}</h5>
      <div className="col-12 pe-0 show-child-on-hover">
        {!editing && (
          <div className="d-flex align-items-center justify-content-between">
            <p className="ellipsis-oneline d-flex break-word">
              {getClientFieldDisplayValueHtml(field, client, false, copyText)}
            </p>
            {!readOnly &&
              field.ManualEdit &&
              CompanyService.canDo("editperson") &&
              !CompanyService.getSetting("hideeditperson") && (
                <i
                  className="fa-regular fa-pen px-2 show-when-hovering-parent pointer"
                  onClick={() => setEditing(true)}
                />
              )}
          </div>
        )}
        {editing && (
          <div className="row">
            <div className="col p-0">
              {field.type === DataType.Date ? (
                <DatePickerEditor
                  onChange={(val) =>
                    (inputValue.current = val
                      ? formatIntizaOldDate(new Date(val))
                      : "")
                  }
                  defaultValue={value?.toString()}
                />
              ) : field.type === DataType.List ? (
                <Dropdown
                  items={dropdownItems}
                  defaultValue={dropdownItemValue}
                  onChange={(value) => (inputValue.current = value.toString())}
                />
              ) : field.type === DataType.Number ? (
                <NumberInput
                  className="form-control"
                  allowDecimal
                  allowNegative
                  defaultValue={value?.toString()}
                  onChange={(e) => (inputValue.current = e.target.value)}
                />
              ) : (
                <input
                  className="form-control"
                  type="text"
                  maxLength={
                    field.type === DataType.Text ? field.maxLength : undefined
                  }
                  defaultValue={value?.toString()}
                  onChange={(e) => (inputValue.current = e.target.value)}
                />
              )}
            </div>

            <div className="col-auto p-0 d-flex align-items-center">
              {saving ? (
                <>
                  <i className="fa-fw fa-solid fa-check react-datepicker__day--outside-month ms-2"></i>
                  <i className="fa-fw fa-solid fa-spinner-third fa-spin third ms-1"></i>
                </>
              ) : (
                <>
                  <i
                    className="fa-fw fa-solid fa-check ms-2 text-primary pointer"
                    onClick={onSave}
                  />
                  <i
                    className="fa-fw fa-solid fa-times ms-1 text-danger pointer"
                    onClick={() => {
                      setEditing(false);
                      inputValue.current = value;
                    }}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </li>
  );
};

function getValue(field: EntityField, client: Client) {
  const displayValue = getClientFieldDisplayValue(field, client);
  if (field.type !== DataType.List) {
    return displayValue;
  }
  return CompanyService.getAdditionalDefinitions()
    .find((x) => x.AdditionalDefinitionID === field.id)!
    .AdditionalDefinitionItems.find((x) => x.Value === displayValue)
    ?.AdditionalDefinitionItemID.toString();
}

export const ClientDetail = () => {
  const { translate, currentLanguage } = TranslationService;
  const [client, setClient] = useState<Client | null | undefined>();
  const [clientIdResult, setClientIdResult] = useState<number>();
  const [agrupadoId, setAgrupadoId] = useState<number>();
  const [loadingAgrupado, setLoadingAgrupado] = useState<boolean>(false);
  const [agrupado, setAgrupado] = useState<Client | null | undefined>();
  const [customerToken, setCustomerToken] = useState<string>();
  const [segments, setSegments] = useState<string>();
  const [companies, setCompanies] = useState<ConsolidateClientCompany[]>([]);
  const [selectedCompanyGuid, setSelectedCompanyGuid] = useState<
    string | undefined
  >(undefined);
  const [clientTotals, setClientTotals] = useState<
    | {
        amount: number;
        overdueAmount: number;
        noDueAmount: number;
        currencySymbol: string;
      }
    | undefined
  >(undefined);
  useContext(AuthenticationContext);

  const { state, search } = useLocation<{
    clientIterator: ClientIterator;
    prevRequest: unknown;
  }>();
  // Consolidado: este detalle siempre opera por personKey (`key`) y usa endpoints /consolidated/*
  const key = new URLSearchParams(search).get("key") ?? undefined;

  const history = useHistory();
  const [showMenu, setShowMenu] = useState(
    StorageService.getPreferences().clientDetailsCollapsed
  );

  const toggleMenu = () => {
    setShowMenu((x) => {
      StorageService.setPreference("clientDetailsCollapsed", !x);
      return !x;
    });
  };

  const collGet = (x: null | HTMLDivElement) => {
    if (x === null) {
      return;
    }
    if (StorageService.getPreferences().clientDetailsCollapsed) {
      x.classList.add("show");
    }
  };

  const loadClient = useCallback(() => {
    const loadClient = async () => {
      if (!key) {
        setClient(null);
        !agrupadoId && setAgrupado(null);
        return;
      }

      if (agrupadoId !== undefined) {
        loadAgrupado();
      }

      const result =
        await ConsolidatedService.getConsolidatedClientDetailRaw(key);
      if (result instanceof Error) {
        setClient(null);
        !agrupadoId && setAgrupado(null);
        return;
      }

      const current = result.Client;
      const companiesList = result.Companies ?? [];
      setCompanies(companiesList);

      const rawAmount = result.Amount ?? 0;
      const rawOverdue = result.OverdueAmount ?? 0;
      setClientTotals({
        amount: rawAmount,
        overdueAmount: rawOverdue,
        noDueAmount: rawAmount - rawOverdue,
        currencySymbol: result.CurrencySymbol ?? "$"
      });

      const initialGuid = companiesList[0]?.CompanyGuid;
      setSelectedCompanyGuid(initialGuid);

      // `client` se usa para facturas (PersonId/Name) y otros componentes del detalle
      const normalizedClient = {
        ...current,
        Name: current?.Name ?? current?.personKey ?? "",
        additionals: Array.isArray(current?.PersonAdditional)
          ? current.PersonAdditional
          : [],
        Person_Segment: Array.isArray(current?.Person_Segment)
          ? current.Person_Segment
          : []
      } as Client;
      setClientIdResult(normalizedClient.PersonId);
      setClient(normalizedClient);
      if (!agrupadoId) {
        setCustomerToken(normalizedClient?.Token ?? "");
      }
    };
    const loadAgrupado = async () => {
      setLoadingAgrupado(true);
      const result = await ClientService.get({ id: agrupadoId!.toString() });
      if (result instanceof Error) {
        setAgrupado(null);
        setLoadingAgrupado(false);
        return;
      }
      setSegments(result.segments?.map((x) => x.Name).join(", "));
      setAgrupado(result.item);
      setCustomerToken(result?.customertoken);
      setLoadingAgrupado(false);
    };
    loadClient();
  }, [agrupadoId, key]);

  // Cuando cambia la empresa seleccionada, cargar detalles tradicionales via /client/details
  useEffect(() => {
    const loadCompanyDetails = async () => {
      if (!selectedCompanyGuid) {
        return;
      }

      const clientId = companies.find(
        (x) => x.CompanyGuid === selectedCompanyGuid
      )?.ClientId;

      if (!clientId) {
        return;
      }

      setClientIdResult(clientId);

      const result = await ClientService.getDetails({
        id: clientId.toString(),
        company: selectedCompanyGuid
      });

      if (result instanceof Error) {
        setAgrupado(null);
        return;
      }

      setSegments(result.segments?.map((x) => x.Name).join(", "));
      setAgrupado({
        ...result.item,
        additionals: Array.isArray(result.item?.additionals)
          ? result.item.additionals
          : [],
        Person_Segment: Array.isArray(result.item?.Person_Segment)
          ? result.item.Person_Segment
          : []
      });
      setCustomerToken(result?.customertoken);
    };
    loadCompanyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyGuid, clientIdResult]);

  useEffect(() => {
    loadClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadClient]);

  const reloads = { invoice: () => undefined, client: loadClient };
  const reload = () => {
    reloads.client();
    reloads.invoice();
  };

  if (client === null || agrupado === null) {
    ModalService.showDefaultModal({
      message: translate.ClientNotFound.replace(
        "{company}",
        CompanyService.getCompanyName()!
      )
    });
    return <Redirect to={"/" + currentLanguage + "/consolidated/clients"} />;
  }

  return (
    <>
      {clientIdResult !== undefined ? (
        <ClientDetailProvider
          reloadExtraData={() => {
            loadClient();
          }}
        >
          <TabPosition module="consolidated/clients">
            <section className="debts" key={clientIdResult ?? key ?? ""}>
              <div>
                {client === undefined || agrupado === undefined ? (
                  <Loading />
                ) : (
                  <div className="row flex-nowrap g-0 app-height">
                    <Helmet>
                      <title>Intiza - {agrupado.Name}</title>
                    </Helmet>
                    <div className="col-auto p-0 debtsMenu h-100">
                      <div
                        className="collapse collapse-horizontal h-100"
                        id="collapseMenu"
                        ref={collGet}
                      >
                        <div className="d-flex flex-column h-100">
                          <div className="resumeCompany">
                            {client?.GroupedPerson &&
                              client.GroupedPerson.length > 0 && (
                                <div
                                  className="viewData row"
                                  style={{ alignItems: "center" }}
                                >
                                  <div className="col-5">
                                    <label style={{ fontSize: "13px" }}>
                                      {
                                        <>
                                          {translate.ViewDataFrom}:
                                          {loadingAgrupado && (
                                            <i className="fas fa-spinner-third fa-spin third ms-2" />
                                          )}
                                        </>
                                      }
                                    </label>
                                  </div>
                                  <div className="col">
                                    <Dropdown
                                      secondaryVariant
                                      items={client.GroupedPerson.map((x) => ({
                                        text: x.Name,
                                        value: x.PersonId
                                      }))}
                                      onChange={setAgrupadoId}
                                      optionLabel={translate.MainCompany}
                                      selectClasses="col"
                                    />
                                  </div>
                                </div>
                              )}
                            {/* Igual que en ClientDetail.tsx: renderizar ambas variantes para no perder el ícono al colapsar */}
                            <ClientDetailLeftMenuButtons
                              client={client}
                              clientEmail={client?.Email}
                              reload={reload}
                              toggleMenu={() => toggleMenu()}
                              direction="column"
                            />
                            <ClientDetailLeftMenuButtons
                              client={client}
                              clientEmail={client?.Email}
                              reload={reload}
                              toggleMenu={() => toggleMenu()}
                              direction="row"
                            />
                          </div>
                          <AboutCompany
                            agrupado={agrupado}
                            showMenu={!!showMenu}
                            customerToken={customerToken}
                            onClientSave={reload}
                            segments={segments}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col debtsDetail h-100 overflow-auto">
                      <div className="ps-3 mb-0 pt-4 position-relative z-index-10">
                        <BreadcrumbHistory
                          defaultPrevious={{
                            link: () => {
                              history.push(
                                `/${TranslationService.currentLanguage}/consolidated/clients`,
                                { prevRequest: state.prevRequest }
                              );
                            },
                            name:
                              state?.clientIterator?.items?.length > 0
                                ? TranslationService.translate.Clients +
                                  ` (${TranslationService.translate.Filtered})`
                                : translate.Clients
                          }}
                          current={{
                            name: client.Name,
                            link: `consolidated/clients`
                          }}
                        >
                          <span className="breadcrumb-controls ms-auto d-flex align-items-center flex-row mb-0">
                            {state?.clientIterator?.hasPrev && (
                              <>
                                {state.clientIterator.hasPrev() && (
                                  <button
                                    className="btn btn-link"
                                    onClick={() => {
                                      setClient(undefined);
                                      state.clientIterator.goPrev(history);
                                    }}
                                  >
                                    {TranslationService.translate.Previous}
                                  </button>
                                )}
                                {state.clientIterator.hasPrev() &&
                                  state.clientIterator.hasNext() && <>|</>}
                                {state.clientIterator.hasNext() && (
                                  <button
                                    className="btn btn-link"
                                    onClick={async () => {
                                      await state.clientIterator.goNext(
                                        history
                                      );
                                      setClient(undefined);
                                    }}
                                  >
                                    {TranslationService.translate.Next}
                                  </button>
                                )}
                              </>
                            )}
                          </span>
                        </BreadcrumbHistory>
                      </div>
                      <FixToTop
                        isFixed
                        backgroundClass=""
                        id="clientTitleHeight"
                      >
                        <div className="ps-3 pe-3 sticky-clientname d-flex justify-content-between align-items-center">
                          <div className={"nameCompany mb-0"}>
                            <h2>{agrupado.Name}</h2>
                          </div>
                          {companies.length > 0 && (
                            <div
                              className="d-flex align-items-center"
                              style={{ minWidth: 200 }}
                            >
                              <Dropdown
                                secondaryVariant
                                items={companies.map((x) => ({
                                  text: x.CompanyName,
                                  value: x.CompanyGuid
                                }))}
                                onChange={(val) =>
                                  setSelectedCompanyGuid(
                                    val ? String(val) : undefined
                                  )
                                }
                                defaultValue={selectedCompanyGuid}
                                selectClasses="col"
                              />
                            </div>
                          )}
                        </div>
                      </FixToTop>
                      {clientTotals && (
                        <div className="dashboard__sales row mb-3 px-3 pt-2">
                          <div className="col-4 pe-0">
                            <div className="dashboard__total card p-3">
                              <div className="card-body px-2">
                                <div className="card-header mb-1">
                                  <p>
                                    {
                                      TranslationService.translate
                                        .TotalReceivables
                                    }
                                  </p>
                                </div>
                                <h2>
                                  {formatCurrencyPortal(
                                    clientTotals.amount,
                                    clientTotals.currencySymbol,
                                    false
                                  )}
                                </h2>
                              </div>
                            </div>
                          </div>
                          <div className="col-4 pe-0">
                            <div className="dashboard__expired card p-3">
                              <div className="card-body px-2">
                                <div className="card-header mb-1">
                                  <p>{TranslationService.translate.Dues}</p>
                                </div>
                                <h2>
                                  {formatCurrencyPortal(
                                    clientTotals.overdueAmount,
                                    clientTotals.currencySymbol,
                                    false
                                  )}
                                </h2>
                              </div>
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="dashboard__notExpired card p-3">
                              <div className="card-body px-2">
                                <div className="card-header mb-1">
                                  <p>{TranslationService.translate.NoDues}</p>
                                </div>
                                <h2>
                                  {formatCurrencyPortal(
                                    clientTotals.noDueAmount,
                                    clientTotals.currencySymbol,
                                    false
                                  )}
                                </h2>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="tab-content p-0" id="pills-tabContent">
                        <div
                          className="tab-pane fade show active"
                          id="pills-collections"
                          role="tabpanel"
                          aria-labelledby="pills-collections-tab"
                        >
                          <div className="card mt-3 p-0">
                            <section className="clientsTabs">
                              <div className="tab-content p-0">
                                {
                                  <div
                                    className="tab-pane fade show active"
                                    id="invoices-tab-pane"
                                    role="tabpanel"
                                    aria-labelledby="home-tab"
                                    tabIndex={0}
                                  >
                                    <InvoiceList
                                      client={client}
                                      consolidatedPk={key!}
                                      setReloads={reloads}
                                      reload={reload}
                                    />
                                  </div>
                                }
                              </div>
                            </section>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </TabPosition>
          <FirstLoadClient
            load={client === undefined || agrupado === undefined}
          />
        </ClientDetailProvider>
      ) : null}
    </>
  );
};

const FirstLoadClient = ({ load }: { load: boolean }) => {
  const clientDetailContextValues = useContext(ClientDetailContext);

  useEffect(() => {
    if (load) {
      clientDetailContextValues.setInvoiceIds([]);
      clientDetailContextValues.setSelectedInvoiceData(new Map());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  return <></>;
};

const AboutCompany = ({
  agrupado,
  showMenu,
  customerToken,
  onClientSave,
  segments
}: {
  agrupado: Client;
  showMenu: boolean;
  customerToken?: string;
  onClientSave?: () => void;
  segments?: string;
}) => {
  const { currentLanguage } = TranslationService;
  const leftColumnFields = useMemo(
    () =>
      [
        ...CompanyService.getAdditionalDefinitionsFiltered(
          Entities.Client,
          false,
          undefined,
          true
        ).map((x) => new EntityField(x)),
        ...CompanyService.getConfigItemsFiltered(Entities.Client, true, [
          ClientFields.Name,
          ClientFields.Contact,
          ClientFields.Email,
          ClientFields.Phone,
          ClientFields.Address,
          ClientFields.Comments
        ]).map((x) => new EntityField(x))
      ].sort(
        sortEntityFields(CompanyService.getPersonSortedFields().split(","))
      ),
    []
  );

  const portalLink =
    `${window.location.origin}/${currentLanguage}/portalclient?t=` +
    customerToken;

  return (
    <>
      <div className="aboutCompany">
        <ul>
          {leftColumnFields.map((field) => (
            <ClientFieldDisplay
              key={field.id}
              field={field}
              client={agrupado}
              showAnyway={false}
              onClientSave={onClientSave}
              readOnly
            />
          ))}
          {agrupado.followUpMail && (
            <ClientFieldDisplayByContent
              title={TranslationService.translate.FollowUpMail}
              value={agrupado.followUpMail}
              tooltip={TranslationService.translate.FollowUpMailTip}
              link={agrupado.followUpMail}
              isMail={true}
              copyText={copyText}
            ></ClientFieldDisplayByContent>
          )}

          {segments ? (
            <>
              <li className="row aboutCompanyItem">
                <h5 className="col-12">
                  {TranslationService.translate.Segments}{" "}
                </h5>
              </li>
              <TooltipComponent title={segments}>
                <div className="col-12 pe-0">
                  <label
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: "1.2em",
                      maxHeight: "3.6em",
                      color: "black",
                      fontWeight: 600,
                      fontSize: 13
                    }}
                  >
                    {segments}
                  </label>
                </div>
              </TooltipComponent>
            </>
          ) : null}
          {CompanyService.getSetting("customerportalenabled") && (
            <ClientFieldDisplayByContent
              title={TranslationService.translate.CustomerSite}
              value={TranslationService.translate.Open}
              link={portalLink}
              personId={agrupado.PersonId}
              copyText={copyText}
            ></ClientFieldDisplayByContent>
          )}
        </ul>
      </div>
    </>
  );
};

export default ClientDetail;
